import { useEffect, useContext, useState } from "react";
import { useMutation } from "@apollo/client";
import {
  SEND_VERIFICATION_CODE_TO_CUSTOMER,
  VERIFY_CUSTOMER_HPHONE_NUMBER,
} from "../../../mutations/phone-validation";
import { GET_ACTIVE_USER } from "../../../queries/auth.queries";
import { UPDATE_USER } from "../../../queries/user.queries";
import { ErrorHandlingContext } from "../../../context/error-handling.context";
import { Digits } from "./verifyNumber.types";

interface UseVerifyNumberParams {
  phoneNumber: string;
  onVerifyFunc?: (digits: Digits) => void;
  onVerified?: () => void;
}

export const useVerifyNumber = ({ phoneNumber, onVerifyFunc, onVerified }: UseVerifyNumberParams) => {
  const [addPhoneScreen, setAddPhoneScreen] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string | null>(null);
  const { setErrorHandlingState } = useContext(ErrorHandlingContext) as unknown as {
    setErrorHandlingState: (state: { showError: boolean; data: string }) => void;
  };

  const [sendVerificationCodeToCustomer, { loading: verificationCodeLoader, error: verificationCodeError }] = useMutation(
    SEND_VERIFICATION_CODE_TO_CUSTOMER,
    {
      variables: {
        phoneNumber,
      },
      onCompleted: (res) => {
        const response = res?.sendVerificationCodeToCustomer;

        if (response?.errorCode) {
          setErrorHandlingState({
            showError: true,
            data: response?.message
          });
        }
      }
    }
  );

  // Verifying the code only confirms it's correct for this number — it
  // doesn't persist the number onto the customer record. Checkout does that
  // via its own follow-up UPDATE_USER call (see personal-info.component.js);
  // this mirrors that so a page refresh doesn't lose the number that was
  // "verified" but never actually saved.
  const [updateUserPhoneNumber, { loading: updateUserPhoneNumberLoader }] = useMutation(
    UPDATE_USER,
    {
      variables: {
        phoneNumber,
      },
      refetchQueries: [{ query: GET_ACTIVE_USER }],
      awaitRefetchQueries: true,
    }
  );

  const [verifyCustomerPhoneNumber, { loading: verifyCustomerPhoneNumberLoader }] = useMutation(
    VERIFY_CUSTOMER_HPHONE_NUMBER,
    {
      variables: {
        phoneNumber,
        verificationCode,
      },
      onCompleted: async (res) => {
        const response = res?.verifyCustomerPhoneNumber;

        if (response?.errorCode) {
          setErrorHandlingState({
            showError: true,
            data: response?.message
          });
          return;
        }

        try {
          await updateUserPhoneNumber();
        } catch (err) {
          setErrorHandlingState({
            showError: true,
            data: (err as Error)?.message
          });
          return;
        }

        setIsVerified(true);
        onVerified?.();
      }
    }
  );

  const onVerify = async (digits: Digits) => {
    if (onVerifyFunc) {
      onVerifyFunc(digits);
      return;
    }

    const code = Object.values(digits).join('');

    if (!code) return;
    setVerificationCode(code);
  };

  useEffect(() => {
    if (!phoneNumber) return;

    const send = async () => {
      try {
        await sendVerificationCodeToCustomer();
      } catch (err) {
        setErrorHandlingState({
          showError: true,
          data: (err as Error)?.message
        });
      }
    };

    send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!verificationCode) return;

    const verify = async () => {
      try {
        await verifyCustomerPhoneNumber();
      } catch (err) {
        setErrorHandlingState({
          showError: true,
          data: (err as Error)?.message
        });
      }
    };

    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode]);

  useEffect(() => {
    if (!verificationCodeError) return;

    setErrorHandlingState({
      showError: true,
      data: verificationCodeError?.message
    });
  }, [verificationCodeError, setErrorHandlingState]);

  const onChangeNumber = (shouldDisplay: boolean) => {
    setAddPhoneScreen(shouldDisplay);
    if (!shouldDisplay) {
      sendVerificationCodeToCustomer();
    }
  };

  const onResend = async () => {
    try {
      await sendVerificationCodeToCustomer();
    } catch (err) {
      setErrorHandlingState({
        showError: true,
        data: (err as Error)?.message
      });
    }
  };

  return {
    addPhoneScreen,
    isVerified,
    onVerify,
    onChangeNumber,
    onResend,
    isLoading: verificationCodeLoader || verifyCustomerPhoneNumberLoader || updateUserPhoneNumberLoader,
  };
};
